import { useState, useRef, useMemo } from 'react';
import { IoPersonCircle } from 'react-icons/io5';
import {
  FaRegTrashAlt,
  FaFilePdf,
  FaFileWord,
  FaFileExcel,
  FaFileImage,
  FaFileAudio,
  FaFileVideo,
  FaFileArchive,
  FaFile,
} from 'react-icons/fa';
import { FiDownload } from 'react-icons/fi';
import { AiOutlineLoading3Quarters } from 'react-icons/ai';
import { MdSend } from 'react-icons/md';
import { BsPaperclip, BsThreeDotsVertical } from 'react-icons/bs';
import { LiaGoogleDrive } from 'react-icons/lia';
import toast from 'react-hot-toast';
import { FaUsers } from 'react-icons/fa';

import useDrivePicker from '../../hooks/useGoogleDrivePicker';
import SunEditorComponent from '../chat/SunEditorComponent';
import { DataState } from '../../context/DataProvider';
import { draftReplyEmail, replyToEmail } from '../../services/email_api';
import {
  google_client_id,
  google_api_key,
  GOOGLE_DRIVE_PICKER_ENABLED,
} from '../../utils/keys';

const getFileIcon = (mimeType) => {
  if (mimeType?.includes('pdf'))
    return <FaFilePdf className="text-red-500" size={20} />;
  if (
    mimeType?.includes('word') ||
    mimeType?.includes('officedocument.wordprocessingml')
  )
    return <FaFileWord className="text-[#5652CF]" size={20} />;
  if (
    mimeType?.includes('excel') ||
    mimeType?.includes('officedocument.spreadsheetml')
  )
    return <FaFileExcel className="text-green-500" size={20} />;
  if (mimeType?.includes('image'))
    return <FaFileImage className="text-yellow-500" size={20} />;
  if (
    mimeType?.includes('zip') ||
    mimeType?.includes('rar') ||
    mimeType?.includes('archive')
  )
    return <FaFileArchive className="text-gray-500" size={20} />;
  if (mimeType?.includes('audio'))
    return <FaFileAudio className="text-purple-500" size={20} />;
  if (mimeType?.includes('video'))
    return <FaFileVideo className="text-indigo-500" size={20} />;
  return <FaFile className="text-gray-500" size={20} />;
};

const AttachmentItem = ({ file, onRemove }) => {
  const [hovered, setHovered] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownloadClick = async () => {
    if (isDownloading || file.from !== 'local') return;
    setIsDownloading(true);

    try {
      const url = URL.createObjectURL(file.file);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.file.name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Download failed. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div
      className="inline-flex h-10 w-40 cursor-pointer items-center justify-center rounded-lg border border-gray-300 p-2 py-3 text-gray-800 shadow-md"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {hovered && file.from === 'local' ? (
        <div className="flex w-full items-center justify-center gap-4">
          {isDownloading ? (
            <AiOutlineLoading3Quarters
              className="animate-spin text-gray-600"
              size={16}
            />
          ) : (
            <FiDownload
              onClick={handleDownloadClick}
              className="cursor-pointer text-gray-600 hover:text-black"
              size={16}
            />
          )}
          <button
            onClick={() => onRemove(file)}
            className="ml-2 text-gray-600 hover:text-red-400"
          >
            &times;
          </button>
        </div>
      ) : (
        <div className="flex w-full items-center space-x-2">
          <div className="flex h-8 w-8 items-center justify-center">
            {getFileIcon(file?.file.mimeType || file?.file.type)}
          </div>
          <span className="truncate text-sm font-medium">
            {file?.file.filename || file?.file.name}
          </span>
          {hovered && (
            <button
              onClick={() => onRemove(file)}
              className="ml-auto text-gray-600 hover:text-red-400"
            >
              &times;
            </button>
          )}
        </div>
      )}
    </div>
  );
};

const ReplyEmail = ({
  onClose,
  sender,
  message_id,
  messageBody,
  setMessageBody,
  localAttachments,
  driveAttachments,
  setLocalAttachments,
  setDriveAttachments,
  customPrompt,
  setCustomPrompt,
  isOutlook,
}) => {
  const fileInputRef = useRef(null);
  const [recepientsEmails, setRecepientsEmails] = useState(
    sender?.map((s) => s.email) || [],
  );
  const [ccs, setCcs] = useState([]);
  const [bccs, setBccs] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [inputCcValue, setInputCcValue] = useState('');
  const [inputBccValue, setInputBccValue] = useState('');
  const [showCc, setShowCc] = useState(false);
  const [showBcc, setShowBcc] = useState(false);
  const [loading, setLoading] = useState(false);
  const [customPromptLoading, setCustomPromptLoading] = useState(false);
  const [openPicker] = useDrivePicker();
  const { userInfo, userGoogleId } = DataState();

  // Just generated Content from AI
  const [justUpdatedValue, setJustUpdatedValue] = useState('');

  // Consolidated attachments for display
  const allAttachments = useMemo(() => {
    const local = localAttachments.map((file) => ({
      name: file.name,
      from: 'local',
      file,
    }));
    const drive = driveAttachments.map((file) => ({
      name: file.name,
      from: 'drive',
      id: file.id,
      file,
    }));
    return [...local, ...drive];
  }, [localAttachments, driveAttachments]);

  const removeEmail = (emailToRemove, type) => {
    if (type === 'to') {
      setRecepientsEmails((prev) =>
        prev.filter((email) => email !== emailToRemove),
      );
    } else if (type === 'cc') {
      setCcs((prev) => prev.filter((email) => email !== emailToRemove));
    } else if (type === 'bcc') {
      setBccs((prev) => prev.filter((email) => email !== emailToRemove));
    }
  };

  const handleKeyDown = (event, type) => {
    const value = (
      type === 'to' ? inputValue : type === 'cc' ? inputCcValue : inputBccValue
    )
      .trim()
      .replace(/,$/, '');
    if (
      (event.key === 'Enter' || event.key === ',' || event.key === ' ') &&
      value
    ) {
      event.preventDefault();
      const emailPattern = /^[a-zA-Z0-9._%+-]+@([a-zA-Z0-9.-]+\.[a-zA-Z]{2,})$/;
      const match = value.match(emailPattern);

      if (match) {
        if (type === 'to') {
          if (!recepientsEmails.includes(value))
            setRecepientsEmails((prev) => [...prev, value]);
          setInputValue('');
        } else if (type === 'cc') {
          if (!ccs.includes(value)) setCcs((prev) => [...prev, value]);
          setInputCcValue('');
        } else if (type === 'bcc') {
          if (!bccs.includes(value)) setBccs((prev) => [...prev, value]);
          setInputBccValue('');
        }
      } else {
        toast.error('Invalid email format!');
      }
    }
  };

  const handleReply = async () => {
    const hasMessage = messageBody && messageBody.trim() !== '';
    const hasLocalFiles = localAttachments.length > 0;
    const hasDriveFiles = driveAttachments.length > 0;

    if (!hasMessage && !hasLocalFiles && !hasDriveFiles) {
      toast('Nothing to send. Please add a message or attachment.');
      return;
    }
    if (recepientsEmails.length === 0) {
      toast.error('Please add at least one recipient.');
      return;
    }
    setLoading(true);

    const formData = new FormData();
    recepientsEmails.forEach((email) => formData.append('recipients', email));
    ccs.forEach((email) => formData.append('ccs', email));
    bccs.forEach((email) => formData.append('bccs', email));

    formData.append('message_id', message_id);
    if (messageBody) {
      formData.append('message_body', messageBody);
    }
    if (localAttachments.length > 0) {
      localAttachments.forEach((file) => {
        formData.append('files', file);
      });
    }
    const driveFileIds = driveAttachments.map((file) => file.id);
    driveFileIds.forEach((id) => formData.append('google_drive_file_ids', id));

    try {
      await replyToEmail(userGoogleId, formData, isOutlook);
      toast.success('Reply sent successfully!');
      setMessageBody('');
      setJustUpdatedValue('clearing');
      setJustUpdatedValue('');

      setLocalAttachments([]);
      setDriveAttachments([]);
      setCustomPrompt('');
      onClose(); // Close the modal after a successful send
    } catch (error) {
      toast.error(error.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDraftReply = async () => {
    if (!customPrompt.trim()) {
      toast.error('Please provide a prompt to generate a draft.');
      return;
    }
    setCustomPromptLoading(true);
    try {
      const response = await draftReplyEmail(
        userGoogleId,
        message_id,
        customPrompt,
        isOutlook,
      );

      const htmlBody = response?.html_body || '';

      setMessageBody(htmlBody);
      setJustUpdatedValue(htmlBody);
    } catch (error) {
      toast.error(error.message || 'Something went wrong. Please try again.');
    } finally {
      setCustomPromptLoading(false);
    }
  };

  const handlePaperClipClick = () => {
    fileInputRef.current.click();
  };

  const handleFileChange = (event) => {
    const files = Array.from(event.target.files);
    setLocalAttachments((prevAttachments) => [...prevAttachments, ...files]);
    event.target.value = null; // Reset the input so the same file can be selected again
  };

  const handleRemove = (fileToRemove) => {
    if (fileToRemove.from === 'local') {
      setLocalAttachments((prev) =>
        prev.filter((file) => file.name !== fileToRemove.name),
      );
    } else if (fileToRemove.from === 'drive') {
      setDriveAttachments((prev) =>
        prev.filter((file) => file.id !== fileToRemove.id),
      );
    }
  };

  const handleTrashClick = () => {
    if (
      !messageBody &&
      !customPrompt &&
      !localAttachments.length &&
      !driveAttachments.length
    ) {
      onClose();
      return;
    }
    setMessageBody('');
    setJustUpdatedValue('clearing');
    setJustUpdatedValue('');

    setCustomPrompt('');
    setLocalAttachments([]);
    setDriveAttachments([]);
    onClose();
  };

  const handleOpenPicker = () => {
    openPicker({
      clientId: google_client_id,
      developerKey: google_api_key,
      viewId: 'DOCS',
      showUploadView: true,
      showUploadFolders: true,
      supportDrives: true,
      multiselect: true,
      callbackFunction: (data) => {
        if (data.action === 'picked') {
          const files = data.docs.map((file) => ({
            id: file.id,
            name: file.name,
            mimeType: file?.mimeType || file?.type,
            url: file.url,
          }));
          setDriveAttachments((prev) => [...prev, ...files]);
        }
      },
    });
  };

  return (
    <div className="flex w-full flex-row transition duration-200">
      <div className="px-4 pl-0">
        {userInfo?.picture ? (
          <img
            src={userInfo.picture}
            alt="User Profile"
            className="h-10 w-10 rounded-full object-cover"
          />
        ) : (
          <IoPersonCircle className="text-gray-700" size={40} />
        )}
      </div>
      <div className="flex w-full flex-col">
        <div className="w-full rounded-xl border border-gray-300 bg-white p-4 shadow-md">
          {/* Corrected structure for the recipients and Cc/Bcc buttons */}
          <div className="mb-3 flex w-full flex-row items-start gap-2 text-gray-800">
            <FaUsers className="mt-2 hidden h-5 w-5 self-center text-gray-600 sm:block" />

            <div className="flex min-h-[40px] w-full flex-grow flex-wrap items-center gap-1 rounded-lg border border-gray-300 p-2 text-sm">
              {recepientsEmails.map((email, index) => (
                <span
                  key={index}
                  className="inline-flex items-center rounded-lg bg-gray-200 px-2 py-1 text-gray-800"
                >
                  {email}
                  <button
                    onClick={() => removeEmail(email, 'to')}
                    className="ml-2 text-gray-600 hover:text-red-400"
                  >
                    &times;
                  </button>
                </span>
              ))}
              <input
                type="text"
                className="min-w-[120px] flex-grow bg-transparent text-gray-800 outline-none"
                placeholder="Enter recipients..."
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={(e) => handleKeyDown(e, 'to')}
              />
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowCc(!showCc)}
                className={`flex min-h-[40px] items-center justify-center rounded-lg border border-gray-300 px-2 py-1 text-sm transition-colors duration-200 hover:bg-gray-200 ${showCc ? 'bg-gray-200' : 'bg-white'} w-full text-center sm:w-auto`}
              >
                Cc
              </button>
              <button
                onClick={() => setShowBcc(!showBcc)}
                className={`flex min-h-[40px] items-center justify-center rounded-lg border border-gray-300 px-2 py-1 text-sm transition-colors duration-200 hover:bg-gray-200 ${showBcc ? 'bg-gray-200' : 'bg-white'} w-full text-center sm:w-auto`}
              >
                Bcc
              </button>
            </div>
          </div>

          {showCc && (
            <div className="mb-3 flex min-h-[40px] w-full flex-wrap items-center gap-1 rounded-lg border border-gray-300 p-2 text-sm">
              {ccs.map((email, index) => (
                <span
                  key={index}
                  className="inline-flex items-center rounded-lg bg-gray-200 px-2 py-1 text-gray-800"
                >
                  {email}
                  <button
                    onClick={() => removeEmail(email, 'cc')}
                    className="ml-2 text-gray-600 hover:text-red-400"
                  >
                    &times;
                  </button>
                </span>
              ))}
              <input
                type="text"
                className="min-w-[120px] flex-grow bg-transparent text-gray-800 outline-none"
                placeholder="Cc..."
                value={inputCcValue}
                onChange={(e) => setInputCcValue(e.target.value)}
                onKeyDown={(e) => handleKeyDown(e, 'cc')}
              />
            </div>
          )}

          {showBcc && (
            <div className="mb-3 flex min-h-[40px] w-full flex-wrap items-center gap-1 rounded-lg border border-gray-300 p-2 text-sm">
              {bccs.map((email, index) => (
                <span
                  key={index}
                  className="inline-flex items-center rounded-lg bg-gray-200 px-2 py-1 text-gray-800"
                >
                  {email}
                  <button
                    onClick={() => removeEmail(email, 'bcc')}
                    className="ml-2 text-gray-600 hover:text-red-400"
                  >
                    &times;
                  </button>
                </span>
              ))}
              <input
                type="text"
                className="min-w-[120px] flex-grow bg-transparent text-gray-800 outline-none"
                placeholder="Bcc..."
                value={inputBccValue}
                onChange={(e) => setInputBccValue(e.target.value)}
                onKeyDown={(e) => handleKeyDown(e, 'bcc')}
              />
            </div>
          )}

          {/* The new SunEditorComponent replacement */}
          <div className="mb-4">
            <SunEditorComponent
              value={messageBody}
              onChange={setMessageBody}
              justUpdatedValue={justUpdatedValue}
            />
          </div>

          {allAttachments.length > 0 && (
            <div className="mt-2 flex flex-grow flex-wrap items-center gap-2 p-1">
              {allAttachments.map((file, index) => (
                <AttachmentItem
                  key={index}
                  file={file}
                  onRemove={handleRemove}
                />
              ))}
            </div>
          )}

          <div className="mt-3 flex flex-col items-center justify-between text-gray-800">
            <div className="flex w-full flex-col items-stretch justify-between space-y-2 sm:flex-row sm:items-center sm:space-x-2 sm:space-y-0">
              <input
                type="text"
                name="custom prompt"
                value={customPrompt}
                placeholder="Add prompt for AI-generated reply draft..."
                onChange={(e) => setCustomPrompt(e.target.value)}
                className="w-full rounded-lg border border-gray-300 bg-white p-2 text-sm focus:outline-none focus:ring-1 focus:ring-gray-300"
              />
              <button
                onClick={handleDraftReply}
                disabled={customPromptLoading}
                className="flex w-full items-center justify-center space-x-2 rounded-md border border-zinc-600 bg-zinc-700 px-4 py-2 text-sm text-white transition hover:border-zinc-800 sm:w-auto"
              >
                <img src="/assets/logo.png" alt="Logo" className="h-5 w-5" />
                {customPromptLoading ? (
                  <span>Generating...</span>
                ) : (
                  <span>Generate</span>
                )}
              </button>
            </div>

            <div className="flex w-full items-center justify-between gap-2 space-x-3 p-2 pl-0 pr-0 sm:gap-0">
              <div className="flex items-center space-x-3">
                <button
                  onClick={handleReply}
                  disabled={loading || recepientsEmails.length === 0}
                  className="flex items-center justify-between rounded-full bg-[#5652CF] px-2 font-medium text-white transition-colors hover:bg-gray-600"
                >
                  <div className="flex h-full items-center justify-center pl-1">
                    <MdSend size={20} />
                  </div>
                  <div className="py-1 pl-2 pr-4 ">
                    {loading ? 'Sending...' : 'Send'}
                  </div>
                </button>
                <div className="flex">
                  {/* eslint-disable-next-line */}
                  <div
                    onClick={handlePaperClipClick}
                    className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md text-gray-600 transition-colors hover:bg-gray-200 hover:text-gray-900"
                  >
                    <BsPaperclip size={18} />
                    <input
                      type="file"
                      ref={fileInputRef}
                      className="hidden"
                      onChange={handleFileChange}
                      multiple
                    />
                  </div>
                  {GOOGLE_DRIVE_PICKER_ENABLED && (
                    // eslint-disable-next-line
                    <div
                      onClick={handleOpenPicker}
                      className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md text-gray-600 transition-colors hover:bg-gray-200 hover:text-gray-900"
                    >
                      <LiaGoogleDrive size={18} />
                    </div>
                  )}
                  <div className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md text-gray-600 transition-colors hover:bg-gray-200 hover:text-gray-900">
                    <BsThreeDotsVertical size={18} />
                  </div>
                </div>
              </div>
              <div className="ml-auto w-auto">
                <button
                  onClick={handleTrashClick}
                  className="text-gray-600 transition-colors hover:text-gray-900"
                >
                  <FaRegTrashAlt size={18} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReplyEmail;
